import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { StatisticsService, StatisticsData } from './statistics.service';
import { GroupService } from 'src/app/core/services/group.service';
import { UserService } from 'src/app/core/services/user.service';
import { Subject, takeUntil } from 'rxjs';
import * as moment from 'moment';
import { SubscriptionService } from 'src/app/core/services/subscription.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import * as QRCode from 'qrcode';
import { FileDownloadService } from 'src/app/core/services/file-download.service';

@Injectable({
  providedIn: 'root'
})
export class StatisticsPdfExportService {

  destroy$ = new Subject<void>();
  group!: any;
  groupDoctors!: any[];
  transactions!: any[];
  currentUser!: any;
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private groupService: GroupService,
    private userService: UserService,
    private subscriptionService: SubscriptionService,
    private http: HttpClient,
    private fileDownloadService: FileDownloadService
  ) {

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe(group => {
        this.group = group;
      });

    this.groupService.groupUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe((users: any[]) => {
        if (users && users.length > 0) {
          this.groupDoctors = users?.filter(u => u.userType === 'doctor' && u.userId !== '511f39e1-ff14-4a6d-b35a-6fcc22923b50') || [];
        }
      });

    this.userService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUser = user;
      });

    this.subscriptionService.transactions$
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        this.transactions = result;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async exportStatisticsToPdf(
    statisticsData: StatisticsData,
    dateRange: string,
    customStartDate: string,//ISOString date
    customEndDate: string, //ISOString date
    statsType: string = 'clinical',
    hospitalId?: string
  ): Promise<{ exportId: string; qrCodeDataUrl: string }> {
    if (!isPlatformBrowser(this.platformId)) {
      console.warn('PDF export is only available in browser environment');
      throw new Error('PDF export is only available in browser environment');
    }

    try {
      // Step 1: Create export record in database
      const exportResponse = await this.http.post(`${environment.API_URL}/api/statistics/export`, {
        groupId: this.group.id,
        startDate: customStartDate,
        endDate: customEndDate,
        statsType,
        hospitalId
      }).toPromise() as any;

      if (!exportResponse.success) {
        throw new Error(exportResponse.message || 'Failed to create export record');
      }

      const exportId = exportResponse.data.exportId;

      // Step 2: Generate QR code
      const baseUrl = window.location.origin;
      const verificationUrl = `${baseUrl}/verify-report/${exportId}`;
      const qrCodeDataUrl = await QRCode.toDataURL(verificationUrl, {
        width: 120,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      // Step 3: Generate PDF with QR code
      const { default: jsPDF } = await import('jspdf');
      await import('jspdf-autotable');

      const doc = new jsPDF();
      let yPosition = 20;
      const margin = 20;
      const pageWidth = doc.internal.pageSize.getWidth();
      const contentWidth = pageWidth - (margin * 2);

      // Add title
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 150, 136);
      this.safeText(doc, `MedicAI Statistics Report`, margin, yPosition);
      yPosition += 15;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(59, 59, 59);
      this.safeText(doc, `${this.group.name}`, margin, yPosition);
      yPosition += 5;
      // Handle provider list with text wrapping
      /* const providerText = this.groupDoctors.map((doctor: any) => `${doctor.title} ${doctor.name} ${doctor.surname} (${doctor.mpNumber})`).join(', ');
      const maxWidth = pageWidth - (margin * 2);
      const wrappedProviderText = this.wrapText(providerText, maxWidth, doc);

      // Split the wrapped text into lines and add each line
      const providerLines = wrappedProviderText.split('\n');
      providerLines.forEach((line: string) => {
        if (line.trim()) {
          this.safeText(doc, line.trim(), margin, yPosition);
          yPosition += 5;
        }
      }); */
      const startDate = moment.utc(customStartDate);
      const endDate = moment.utc(customEndDate);
      const dateRangeText = `Date Range: ${startDate.format('DD/MM/YYYY')} - ${endDate.format('DD/MM/YYYY')}`;
      this.safeText(doc, dateRangeText, margin, yPosition);
      yPosition += 20;

      // Add QR code in top right corner
      const qrSize = 40;
      const qrX = pageWidth - margin - qrSize;
      const qrY = 15;
      doc.addImage(qrCodeDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);

      // Add QR code label (centered)
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      const labelText = 'Scan to verify';
      const labelWidth = doc.getTextWidth(labelText);
      const labelX = qrX + (qrSize - labelWidth) / 2;
      this.safeText(doc, labelText, labelX, qrY + qrSize + 5);

      doc.setTextColor(0, 0, 0);

      // Add summary statistics
      yPosition = this.addSectionHeader(doc, 'Summary Statistics', yPosition, margin);

      const summaryData = [
        ['Metric', 'Value'],
        ['Patients Seen', statisticsData.clinical?.patientsSeen?.toString() || '0'],
        ['Total Appointments', statisticsData.appointmentTypeOverview?.totalAppointments?.toString() || '0']
      ];

      (doc as any).autoTable({
        startY: yPosition,
        head: [summaryData[0]],
        body: summaryData.slice(1),
        margin: { left: margin, right: margin },
        styles: { fontSize: 10 },
        headStyles: { fillColor: [0, 150, 136], textColor: 255 }
      });

      yPosition = (doc as any).lastAutoTable.finalY + 15;

      // Add patient statistics
      if (statisticsData.clinical?.genderBreakdown && Object.keys(statisticsData.clinical.genderBreakdown).length > 0) {
        yPosition = this.addSectionHeader(doc, 'Patient Gender Statistics', yPosition, margin);

        const patientData = Object.entries(statisticsData.clinical.genderBreakdown).map(([key, value]) => [
          key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
          value.toString()
        ]);

        (doc as any).autoTable({
          startY: yPosition,
          head: [['Metric', 'Value']],
          body: patientData,
          margin: { left: margin, right: margin },
          styles: { fontSize: 10 },
          headStyles: { fillColor: [0, 150, 136], textColor: 255 }
        });

        yPosition = (doc as any).lastAutoTable.finalY + 15;
      }

      // Add referral source statistics
      if (statisticsData.clinical?.referralSources) {
        yPosition = this.addSectionHeader(doc, 'Patient Referral Sources', yPosition, margin);

        const referralData = [
          ['With Referral Source', statisticsData.clinical.referralSources.withReferralSource?.toString() || '0'],
          ['Without Referral Source', statisticsData.clinical.referralSources.withoutReferralSource?.toString() || '0'],
          ['Total Patients', statisticsData.clinical.referralSources.total?.toString() || '0']
        ];

        (doc as any).autoTable({
          startY: yPosition,
          head: [['Metric', 'Value']],
          body: referralData,
          margin: { left: margin, right: margin },
          styles: { fontSize: 10 },
          headStyles: { fillColor: [0, 150, 136], textColor: 255 }
        });

        yPosition = (doc as any).lastAutoTable.finalY + 15;

        // Add top referral sources if available
        if (statisticsData.clinical.referralSources.sources && statisticsData.clinical.referralSources.sources.length > 0) {
          yPosition = this.addSubSectionHeader(doc, 'Top Referral Sources', yPosition, margin);

          const topSourcesData = statisticsData.clinical.referralSources.sources.map(source => [
            source.source,
            source.count.toString()
          ]);

          (doc as any).autoTable({
            startY: yPosition,
            head: [['Referral Source', 'Count']],
            body: topSourcesData,
            margin: { left: margin, right: margin },
            styles: { fontSize: 10 },
            headStyles: { fillColor: [0, 150, 136], textColor: 255 }
          });

          yPosition = (doc as any).lastAutoTable.finalY + 15;
        }
      }

      // Add appointment statistics
      if (statisticsData.appointmentTypeOverview) {
        yPosition = this.addSectionHeader(doc, 'Appointment Statistics', yPosition, margin);

        const appointmentData = [
          ['Total Appointments', statisticsData.appointmentTypeOverview.totalAppointments?.toString() || '0'],
          ['Total Cancelled', statisticsData.appointmentTypeOverview.totalCancelled?.toString() || '0'],
          ['Total Did Not Attend', statisticsData.appointmentTypeOverview.totalDidNotAttend?.toString() || '0'],
          ['Average Waiting Time', `${statisticsData.appointmentTypeOverview.overallAvgWaitingTime?.toFixed(1) || '0'} min`]
        ];

        (doc as any).autoTable({
          startY: yPosition,
          head: [['Metric', 'Value']],
          body: appointmentData,
          margin: { left: margin, right: margin },
          styles: { fontSize: 10 },
          headStyles: { fillColor: [0, 150, 136], textColor: 255 }
        });

        yPosition = (doc as any).lastAutoTable.finalY + 15;
      }

      // Add procedure statistics
      if (statisticsData.procedures) {
        yPosition = this.addSectionHeader(doc, 'Procedure Statistics', yPosition, margin);

        const procedureData = [
          ['Total Procedures', statisticsData.procedures.totalProcedures?.toString() || '0']
        ];

        (doc as any).autoTable({
          startY: yPosition,
          head: [['Metric', 'Value']],
          body: procedureData,
          margin: { left: margin, right: margin },
          styles: { fontSize: 10 },
          headStyles: { fillColor: [0, 150, 136], textColor: 255 }
        });

        yPosition = (doc as any).lastAutoTable.finalY + 15;
      }

      // Add consenting overview
      yPosition = this.addSectionHeader(doc, 'Consenting Overview', yPosition, margin);

      // Onboarding Consent subsection (always included if data exists)
      if (statisticsData.procedures?.onboardingConsents) {
        yPosition = this.addSubSectionHeader(doc, 'Onboarding Consent', yPosition, margin);

        const onboardingConsentData = [
          ['Consents Completed', statisticsData.procedures.onboardingConsents.completed?.toString() || '0'],
          ['Consents Pending', statisticsData.procedures.onboardingConsents.pending?.toString() || '0'],
          ['Consents Not Sent', statisticsData.procedures.onboardingConsents.notSent?.toString() || '0'],
          ['Compliance Percentage', `${statisticsData.procedures.onboardingConsents.compliancePercentage?.toFixed(1) || '0'}%`]
        ];

        (doc as any).autoTable({
          startY: yPosition,
          head: [['Metric', 'Value']],
          body: onboardingConsentData,
          margin: { left: margin, right: margin },
          styles: { fontSize: 10 },
          headStyles: { fillColor: [0, 150, 136], textColor: 255 }
        });

        yPosition = (doc as any).lastAutoTable.finalY + 15;
      }

      // Procedural Consent subsection (only if procedures exist)
      if (statisticsData.procedures?.procedureConsents) {
        yPosition = this.addSubSectionHeader(doc, 'Procedural Consent', yPosition, margin);

        const proceduralConsentData = [
          ['Consents Completed', statisticsData.procedures.procedureConsents.completed?.toString() || '0'],
          ['Consents Pending', statisticsData.procedures.procedureConsents.pending?.toString() || '0'],
          ['Consents Not Sent', statisticsData.procedures.procedureConsents.notSent?.toString() || '0'],
          ['Compliance Percentage', `${statisticsData.procedures.procedureConsents.compliancePercentage?.toFixed(1) || '0'}%`]
        ];

        (doc as any).autoTable({
          startY: yPosition,
          head: [['Metric', 'Value']],
          body: proceduralConsentData,
          margin: { left: margin, right: margin },
          styles: { fontSize: 10 },
          headStyles: { fillColor: [0, 150, 136], textColor: 255 }
        });

        yPosition = (doc as any).lastAutoTable.finalY + 15;
      }

      //Transactions within the date range
      let filteredTransactions = this.transactions?.filter((transaction: any) => moment(transaction.createdAt).isBetween(startDate, endDate, null, '[]'));
      if (filteredTransactions && filteredTransactions.length > 0) {
        yPosition = this.addSectionHeader(doc, 'Transactions', yPosition, margin);

        // Calculate total amount spent
        const totalAmount = filteredTransactions.reduce((sum: number, transaction: any) => sum + parseFloat(transaction.amount), 0);

        const transactionData = filteredTransactions.map((transaction: any) => [
          this.getReadableTransactionType(transaction.type),
          `R ${transaction.amount}`
        ]);

        // Add totals row
        transactionData.push(['TOTAL SPENT', `R ${totalAmount}`]);

        (doc as any).autoTable({
          startY: yPosition,
          head: [['Transaction Type', 'Amount']],
          body: transactionData,
          margin: { left: margin, right: margin },
          styles: { fontSize: 10 },
          headStyles: { fillColor: [0, 150, 136], textColor: 255 },
          bodyStyles: {
            fontSize: 10,
            fontStyle: 'bold'
          },
          didParseCell: function (data: any) {
            if (data.row.index === transactionData.length - 1) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [240, 240, 240];
              data.cell.styles.textColor = [0, 0, 0];
            }
          }
        });

        yPosition = (doc as any).lastAutoTable.finalY + 15;
      }

      // Add footer with "Generated by" message
      this.addFooter(doc, margin);

      // Step 4: Generate PDF blob and upload to Azure
      const pdfBlob = doc.output('blob');
      const fileName = `statistics-report-${startDate.toISOString().split('T')[0]}-to-${endDate.toISOString().split('T')[0]}.pdf`;

      // Upload to Azure storage
      const uploadResponse = await this.uploadPdfToAzure(pdfBlob, fileName, exportId);

      // Step 5: Update export record with document URL and QR code data
      await this.http.put(`${environment.API_URL}/api/statistics/export/${exportId}`, {
        documentUrl: uploadResponse.documentUrl,
        fileName: fileName,
        fileSize: pdfBlob.size,
        qrCodeData: qrCodeDataUrl,
        status: 'completed'
      }).toPromise();

      // Step 6: Download PDF in browser
      const blob = doc.output('blob') as Blob;
      await this.fileDownloadService.download(blob, fileName);

      return {
        exportId,
        qrCodeDataUrl
      };

    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }

  // Safe text method that handles jsPDF v2.x correctly
  private safeText(doc: any, text: string, x: number, y: number, options?: any): void {
    // Validate inputs
    if (!doc || typeof doc.text !== 'function') {
      console.error('Invalid jsPDF document object:', doc);
      return;
    }

    if (!text || typeof text !== 'string') {
      console.warn('Invalid text parameter:', text);
      text = ' ';
    }

    if (typeof x !== 'number' || typeof y !== 'number' || isNaN(x) || isNaN(y)) {
      console.warn('Invalid coordinates:', x, y);
      return;
    }

    // Log the parameters being passed

    try {
      // For jsPDF v2.x, the text method signature is different
      // Try the new v2.x signature first: text(text, x, y, options)
      if (options && typeof options === 'object') {
        doc.text(text, x, y, options);
      } else {
        doc.text(text, x, y);
      }
    } catch (error) {
      console.warn('Error with v2.x method, trying v1.x signature:', error);
      try {
        // Try the old v1.x signature: text(text, x, y, options)
        if (options && typeof options === 'object') {
          doc.text(text, x, y, options);
        } else {
          doc.text(text, x, y);
        }
      } catch (fallbackError) {
        console.error('Both v2.x and v1.x methods failed:', fallbackError);

        // Try alternative approaches for jsPDF v2.x
        try {
          // Some jsPDF v2.x versions might use a different approach
          if (doc.putText) {
            doc.putText(text, x, y);
          } else if (doc.addText) {
            doc.addText(text, x, y);
          } else {
            // Last resort: try to add text at origin
            doc.text(text || ' ', 0, 0);
          }
        } catch (finalError) {
          console.error('All text methods failed:', finalError);
          // If all else fails, try to add text with minimal parameters
          try {
            doc.text(text || ' ', 0, 0);
          } catch (lastError) {
            console.error('Complete failure adding text:', lastError);
          }
        }
      }
    }
  }

  private addSectionHeader(doc: any, title: string, yPosition: number, margin: number): number {
    // Check if we need a new page
    if (yPosition > doc.internal.pageSize.getHeight() - 40) {
      doc.addPage();
      yPosition = 20;
    }

    // Validate title parameter
    if (!title || typeof title !== 'string') {
      title = 'Section';
    }

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 150, 136); // #009688 color (RGB: 0, 150, 136)
    this.safeText(doc, title, margin, yPosition);
    yPosition += 8;

    return yPosition;
  }

  private addSubSectionHeader(doc: any, title: string, yPosition: number, margin: number): number {
    // Check if we need a new page
    if (yPosition > doc.internal.pageSize.getHeight() - 40) {
      doc.addPage();
      yPosition = 20;
    }

    // Validate title parameter
    if (!title || typeof title !== 'string') {
      title = 'Subsection';
    }

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 150, 136); // #009688 color (RGB: 0, 150, 136)
    this.safeText(doc, title, margin, yPosition);
    yPosition += 6;

    return yPosition;
  }

  private wrapText(text: string, maxWidth: number, doc: any): string {
    if (!text || text.length === 0) return '';

    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = doc.getTextWidth(testLine);

      if (testWidth > maxWidth && currentLine !== '') {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines.join('\n');
  }

  private getReadableTransactionType(type: string): string {
    const transactionTypeMap: { [key: string]: string } = {
      'subscription': 'Subscription Payment',
      'token-recharge': 'Token Recharge',
      'token-auto-recharge': 'Auto Token Recharge',
      'text-message-credit-recharge': 'Text Message Credit Recharge',
      'text-message-credit-auto-recharge': 'Auto Text Message Credit Recharge'
    };

    return transactionTypeMap[type] || type;
  }

  /**
   * Upload PDF to Azure storage
   */
  private async uploadPdfToAzure(pdfBlob: Blob, fileName: string, exportId: string): Promise<{ documentUrl: string }> {
    try {
      // Get upload URL from server
      const uploadUrlResponse = await this.http.post(`${environment.API_URL}/api/media/get-upload-url`, {
        patientId: 'statistics-export', // Use a generic identifier for statistics exports
        jobId: exportId,
        fileName: fileName
      }).toPromise() as any;

      if (!uploadUrlResponse || !uploadUrlResponse.uploadUrl) {
        throw new Error('Failed to get upload URL: ' + (uploadUrlResponse?.message || 'Unknown error'));
      }

      const uploadUrl = uploadUrlResponse.uploadUrl;
      const blobName = uploadUrlResponse.blobName;
      const containerName = uploadUrlResponse.containerName;

      // Upload file to Azure
      await this.http.put(uploadUrl, pdfBlob, {
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': 'application/pdf'
        }
      }).toPromise();

      // Generate a read-only document URL for PDF viewing
      const documentUrl = `${environment.API_URL}/api/download-report/${exportId}`;

      return { documentUrl };

    } catch (error) {
      console.error('Error uploading PDF to Azure:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new Error(`Failed to upload PDF to Azure storage: ${errorMessage}`);
    }
  }

  /**
   * The Recognition Export: a month-by-month statistics breakdown over a chosen range.
   *
   * `startDate`/`endDate` are optional and the server falls back to the last 12 whole months
   * when both are absent, which is exactly what this produced before the range existed. The
   * range the PDF prints is the one the SERVER returns, not the one that was asked for — it
   * snaps to whole calendar months, because that is what the table underneath actually reports.
   *
   * Still QR-verified, unchanged.
   */
  async exportTwelveMonthStatisticsToPdf(
    statsType: string = 'clinical',
    hospitalId?: string,
    socketId?: string,
    // The Recognition Export's range. OPTIONAL, and the server falls back to the last 12 whole
    // months when both are absent — so an older caller (or a retry built from a stale payload)
    // still gets exactly the report this endpoint has always produced.
    // Named apart from the `startDate`/`endDate` consts further down, which hold the range the
    // SERVER resolved. Same concept, different authority — and one shadowing the other made the
    // request body silently reference the wrong one.
    rangeStart?: string,
    rangeEnd?: string,
    /**
     * Report variant.
     *
     * `includeConsent: false` is the EthiQal Recognition report — the same figures over the same
     * monthly breakdown, with every consent row, column and explanation removed. One method with
     * a flag rather than a second copy: the two differ only in what they omit, and a fork would
     * mean every future change to the shared 90% being made twice.
     */
    options: { includeConsent?: boolean; reportTitle?: string; filePrefix?: string } = {}
  ): Promise<{ exportId: string; qrCodeDataUrl: string }> {
    const includeConsent = options.includeConsent !== false;
    const reportTitle = options.reportTitle || 'MedicAI Recognition Report';
    const filePrefix = options.filePrefix || 'recognition-report';
    if (!isPlatformBrowser(this.platformId)) {
      console.warn('PDF export is only available in browser environment');
      throw new Error('PDF export is only available in browser environment');
    }

    try {
      // Step 1: Create 12-month export record in database
      const exportResponse = await this.http.post(`${environment.API_URL}/api/statistics/export/12month`, {
        groupId: this.group.id,
        statsType,
        hospitalId,
        socketId,
        startDate: rangeStart,
        endDate: rangeEnd
      }).toPromise() as any;

      if (!exportResponse.success) {
        throw new Error(exportResponse.message || 'Failed to create 12-month export record');
      }

      const exportId = exportResponse.data.exportId;
      const dateRange = exportResponse.data.dateRange;

      // Step 2: Generate QR code
      const baseUrl = window.location.origin;
      const verificationUrl = `${baseUrl}/verify-report/${exportId}`;
      const qrCodeDataUrl = await QRCode.toDataURL(verificationUrl, {
        width: 120,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      // Step 3: Generate PDF with QR code in landscape orientation
      const { default: jsPDF } = await import('jspdf');
      await import('jspdf-autotable');

      const doc = new jsPDF('landscape', 'mm', 'a4');
      let yPosition = 20;
      const margin = 10; // Reduced from 20 to 10
      const pageWidth = doc.internal.pageSize.getWidth();
      const contentWidth = pageWidth - (margin * 2);

      // Add title
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 150, 136);
      this.safeText(doc, reportTitle, margin, yPosition);
      yPosition += 15;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(59, 59, 59);
      this.safeText(doc, `${this.group.name}`, margin, yPosition);
      yPosition += 5;

      // Add date range
      const startDate = moment.utc(dateRange.startDate);
      const endDate = moment.utc(dateRange.endDate);
      const dateRangeText = `Date Range: ${startDate.format('DD/MM/YYYY')} - ${endDate.format('DD/MM/YYYY')}`;
      this.safeText(doc, dateRangeText, margin, yPosition);
      yPosition += 20;

      // Add QR code in top right corner
      const qrSize = 40;
      const qrX = pageWidth - margin - qrSize;
      const qrY = 15;
      doc.addImage(qrCodeDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);

      // Add QR code label (centered)
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      const labelText = 'Scan to verify';
      const labelWidth = doc.getTextWidth(labelText);
      const labelX = qrX + (qrSize - labelWidth) / 2;
      this.safeText(doc, labelText, labelX, qrY + qrSize + 5);

      doc.setTextColor(0, 0, 0);

      // ⚠ The heading and the note are DERIVED FROM THE RANGE THE SERVER RETURNED, not from the
      // range that was requested and not from a fixed string. The server snaps the request to
      // whole calendar months, so a report that repeated the request would put dates on the page
      // that its own monthly figures contradict — and this one used to say "the last 12 months"
      // unconditionally, which stopped being true the moment the range became a choice.
      const monthCount = endDate.diff(startDate, 'months') + 1;
      const periodLabel = monthCount === 1
        ? startDate.format('MMMM YYYY')
        : `${startDate.format('MMMM YYYY')} – ${endDate.format('MMMM YYYY')} (${monthCount} months)`;

      yPosition = this.addSectionHeader(doc, `Detailed Statistics Report — ${periodLabel}`, yPosition, margin);

      const noteText = `This report contains detailed monthly statistics for ${periodLabel} of practice data. Figures are reported per whole calendar month.`;
      const wrappedNoteText = this.wrapText(noteText, contentWidth, doc);
      const noteLines = wrappedNoteText.split('\n');
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      noteLines.forEach((line: string) => {
        if (line.trim()) {
          this.safeText(doc, line.trim(), margin, yPosition);
          yPosition += 5;
        }
      });
      yPosition += 15;

      // Add detailed monthly statistics table
      if (exportResponse.data.monthlyStats) {
        yPosition = this.addDetailedMonthlyTable(doc, exportResponse.data.monthlyStats, yPosition, margin, includeConsent);
      }

      // Add monthly transactions breakdown
      yPosition = this.addMonthlyTransactionsTable(doc, startDate, endDate, yPosition, margin);

      // Add disclaimer page
      yPosition = this.addDisclaimerPage(doc, yPosition, margin, includeConsent);

      // Add footer with "Generated by" message
      this.addFooter(doc, margin);

      // Step 4: Generate PDF blob and upload to Azure
      const pdfBlob = doc.output('blob');
      const fileName = `${filePrefix}-${startDate.toISOString().split('T')[0]}-to-${endDate.toISOString().split('T')[0]}.pdf`;

      // Upload to Azure storage
      const uploadResponse = await this.uploadPdfToAzure(pdfBlob, fileName, exportId);

      // Step 5: Update export record with document URL and QR code data
      await this.http.put(`${environment.API_URL}/api/statistics/export/${exportId}`, {
        documentUrl: uploadResponse.documentUrl,
        fileName: fileName,
        fileSize: pdfBlob.size,
        qrCodeData: qrCodeDataUrl,
        status: 'completed'
      }).toPromise();

      // Step 6: Download PDF in browser
      const blob = doc.output('blob') as Blob;
      await this.fileDownloadService.download(blob, fileName);

      return {
        exportId,
        qrCodeDataUrl
      };

    } catch (error) {
      console.error('Error generating 12-month report:', error);
      throw error;
    }
  }

  /**
   * Add detailed monthly statistics table
   */
  private addDetailedMonthlyTable(
    doc: any,
    monthlyStats: any,
    yPosition: number,
    margin: number,
    includeConsent: boolean = true,
  ): number {
    const { monthlyData, firstHalfAverage, secondHalfAverage } = monthlyStats;

    if (!monthlyData || monthlyData.length === 0) {
      return yPosition;
    }

    // Check if we need a new page
    if (yPosition > doc.internal.pageSize.getHeight() - 100) {
      doc.addPage();
      yPosition = 20;
    }

    // Create table data
    const tableData = [];

    // Header row
    const months = monthlyData.map((month: any) => month.month);
    const headerRow = ['Metric', ...months];
    tableData.push(headerRow);

    // General Statistics
    tableData.push(['# patient consultations', ...monthlyData.map((month: any) => month.patientConsultations.toString())]);
    tableData.push(['# AI transcriptions', ...monthlyData.map((month: any) => month.aiTranscriptions.toString())]);
    tableData.push(['# clinical notes (AI and non-AI)', ...monthlyData.map((month: any) => month.clinicalNotes.toString())]);
    tableData.push(['# procedures', ...monthlyData.map((month: any) => month.procedures.toString())]);

    // ── Consent, omitted wholesale for the EthiQal variant ──────────────────────────
    //
    // The spacer rows are inside the guard too. Left outside, a consent-free report would carry
    // three blank bands where the consent blocks used to be — which reads as data that failed to
    // load rather than as a report that never had it.
    if (includeConsent) {
      // Empty row for spacing
      tableData.push(['', ...months.map(() => '')]);

      // Onboarding Consents
      tableData.push(['Onboarding Consents - Completed', ...monthlyData.map((month: any) => month.onboardingConsents.completed.toString())]);
      tableData.push(['Onboarding Consents - Pending', ...monthlyData.map((month: any) => month.onboardingConsents.pending.toString())]);
      tableData.push(['Onboarding Consents - Not Sent', ...monthlyData.map((month: any) => month.onboardingConsents.notSent.toString())]);

      // Empty row for spacing
      tableData.push(['', ...months.map(() => '')]);

      // Procedure Consents
      tableData.push(['Procedure Consents - Completed', ...monthlyData.map((month: any) => month.procedureConsents.completed.toString())]);
      tableData.push(['Procedure Consents - Pending', ...monthlyData.map((month: any) => month.procedureConsents.pending.toString())]);
      tableData.push(['Procedure Consents - Not Sent', ...monthlyData.map((month: any) => month.procedureConsents.notSent.toString())]);
    }

    // Empty row for spacing
    tableData.push(['', ...months.map(() => '')]);

    // Summary Percentages
    tableData.push(['% AI scribed as % of patient consultations', ...monthlyData.map((month: any) => `${month.aiScribedPercentage}%`)]);
    if (includeConsent) {
      tableData.push(['Consent form onboarding - compliance %', ...monthlyData.map((month: any) => `${month.onboardingCompliancePercentage}%`)]);
      tableData.push(['Consent form procedures - compliance %', ...monthlyData.map((month: any) => `${month.procedureCompliancePercentage}%`)]);
    }

    // Empty row for spacing
    tableData.push(['', ...months.map(() => '')]);

    // ── 6-monthly averages ──────────────────────────────────────────────────────────
    //
    // ⚠ BUILT FROM THE ACTUAL MONTH COUNT, not from a hardcoded 12.
    //
    // This used to slice `0..6` and `6..12` and emit a cell for each half unconditionally. That
    // was safe while the report was always exactly 12 months. It is not safe now the range is
    // chosen: over six months `secondHalf` is EMPTY, the server's `calculateSixMonthAverage([])`
    // returns `{}`, and the row rendered a literal `undefined%` — in a cell one column past the
    // end of the header, because the row was built to a fixed width the table no longer had.
    //
    // So: one cell per month, an average parked on the last month of each half, and no second
    // half at all when there is not one.
    const monthTotal = months.length;
    const firstHalfCount = Math.min(6, monthTotal);
    const hasSecondHalf = monthTotal > 6;

    const halfRow = ['6-mnthly result', ...months.map(() => '')];
    tableData.push(halfRow);

    const averageRow = ['AI Scribed %', ...months.map(() => '')];
    // `+1` throughout: index 0 is the metric label, so month N lives at N+1.
    if (firstHalfCount > 0 && firstHalfAverage?.aiScribedPercentage !== undefined) {
      averageRow[firstHalfCount] = `${firstHalfAverage.aiScribedPercentage}%`;
    }
    if (hasSecondHalf && secondHalfAverage?.aiScribedPercentage !== undefined) {
      averageRow[monthTotal] = `${secondHalfAverage.aiScribedPercentage}%`;
    }
    tableData.push(averageRow);

    // The metric column carries labels like "% AI scribed as % of patient consultations", so it
    // keeps a generous fixed share; the months divide whatever is left. Floored so that a
    // hypothetical very long range degrades to a scrollable-looking squeeze rather than to
    // negative widths.
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - (margin * 2);
    const metricWidth = Math.min(60, Math.max(40, contentWidth * 0.22));
    const monthWidth = Math.max(10, (contentWidth - metricWidth) / months.length);

    // Create the table
    (doc as any).autoTable({
      startY: yPosition,
      head: [tableData[0]],
      body: tableData.slice(1),
      margin: { left: margin, right: margin },
      styles: {
        fontSize: 8,
        cellPadding: 2
      },
      headStyles: {
        fillColor: [0, 150, 136],
        textColor: 255,
        fontSize: 8,
        fontStyle: 'bold'
      },
      // ⚠ WIDTHS ARE DERIVED FROM THE PAGE AND THE MONTH COUNT, not fixed.
      //
      // These were `50` and `18` — numbers chosen when the report was always twelve months, which
      // happen to fill a landscape A4. Once the range became a choice they stopped filling
      // anything: a six-month report drew a table across a little over half the page and left the
      // rest white, which reads as a broken layout rather than a shorter report.
      columnStyles: {
        0: { cellWidth: metricWidth },
        ...months.reduce((acc: any, _: any, index: number) => {
          acc[index + 1] = { cellWidth: monthWidth };
          return acc;
        }, {})
      },
      didDrawCell: function (data: any) {
        // Add alternating row colors
        if (data.row.index > 0 && data.row.index % 2 === 0) {
          data.cell.styles.fillColor = [248, 248, 248];
        }

        // Highlight summary rows
        if (data.row.raw[0].includes('%') || data.row.raw[0].includes('6-mnthly')) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [240, 240, 240];
        }
      }
    });

    return (doc as any).lastAutoTable.finalY + 15;
  }

  /**
   * Add monthly transactions breakdown table with 12 columns
   */
  private addMonthlyTransactionsTable(doc: any, startDate: moment.Moment, endDate: moment.Moment, yPosition: number, margin: number): number {
    // Filter transactions within the date range
    const filteredTransactions = this.transactions?.filter((transaction: any) =>
      moment(transaction.createdAt).isBetween(startDate, endDate, null, '[]')
    );

    if (!filteredTransactions || filteredTransactions.length === 0) {
      return yPosition;
    }

    // ⚠ ONE COLUMN PER MONTH IN THE RANGE — this used to be a hardcoded twelve.
    //
    // With a fixed 12-month report that was the same thing. It is not any more: a six-month
    // EthiQal export rendered Jan–Jun of real figures followed by Jul–Dec of R 0, which is not a
    // short report — it is a report that states, in a table, that the practice billed nothing for
    // six months it was never asked about.
    const monthlyData: any[] = [];
    const currentDate = startDate.clone().startOf('month');
    const lastMonth = endDate.clone().endOf('month');

    while (currentDate.isSameOrBefore(lastMonth, 'month')) {
      const monthStart = currentDate.clone().startOf('month');
      const monthEnd = currentDate.clone().endOf('month');
      const monthKey = currentDate.format('YYYY-MM');
      const monthName = currentDate.format('MMM-YY');

      // Get transactions for this month
      const monthTransactions = filteredTransactions.filter(transaction =>
        moment(transaction.createdAt).isBetween(monthStart, monthEnd, null, '[]')
      );

      // Calculate subscription and AI token costs
      let subscriptionTotal = 0;
      let aiTokenTotal = 0;

      monthTransactions.forEach(transaction => {
        const amount = parseFloat(transaction.amount);
        if (transaction.type === 'subscription') {
          subscriptionTotal += amount;
        } else if (transaction.type.includes('token')) {
          aiTokenTotal += amount;
        }
      });

      monthlyData.push({
        month: monthName,
        subscription: subscriptionTotal,
        aiTokens: aiTokenTotal,
        total: subscriptionTotal + aiTokenTotal
      });

      currentDate.add(1, 'month');
    }

    // Add section header
    yPosition = this.addSectionHeader(doc, 'Monthly Transactions Breakdown', yPosition, margin);

    // Create table data with 12 columns
    const tableData = [
      // Subscription row
      [
        'Subscription',
        ...monthlyData.map(month => `R ${month.subscription.toFixed(0)}`)
      ],
      // AI Tokens row
      [
        'AI Tokens',
        ...monthlyData.map(month => `R ${month.aiTokens.toFixed(0)}`)
      ],
      // Total row
      [
        'Total',
        ...monthlyData.map(month => `R ${month.total.toFixed(0)}`)
      ]
    ];

    // Create column headers (metric + 12 months)
    const headers = [
      'Transaction Type',
      ...monthlyData.map(month => month.month)
    ];

    const txPageWidth = doc.internal.pageSize.getWidth();
    const txContentWidth = txPageWidth - (margin * 2);
    const txMetricWidth = Math.min(60, Math.max(40, txContentWidth * 0.22));
    const txMonthWidth = Math.max(10, (txContentWidth - txMetricWidth) / Math.max(1, monthlyData.length));

    // Add table
    (doc as any).autoTable({
      startY: yPosition,
      head: [headers],
      body: tableData,
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [0, 150, 136], textColor: 255, fontSize: 8 },
      bodyStyles: {
        fontSize: 8,
        fontStyle: 'bold',
        cellPadding: 2
      },
      // Same derivation as the detailed table above, and for the same reason.
      columnStyles: {
        0: { cellWidth: txMetricWidth },
        ...monthlyData.reduce((acc: any, _: any, index: number) => {
          acc[index + 1] = { cellWidth: txMonthWidth };
          return acc;
        }, {})
      }
    });

    return (doc as any).lastAutoTable.finalY + 15;
  }

  /**
   * The methodology page: every metric on the report, explained.
   *
   * ─── WHY THIS IS DATA AND NOT 200 `safeText` CALLS ──────────────────────────────────
   *
   * It used to be one hand-placed line per row of prose, each wrapped by hand to a column width
   * that was itself hardcoded. Three things followed from that, and all three were live defects:
   *
   *   1. Dropping the consent column left the page three-quarters full, because the remaining
   *      columns could not reflow into the gap — their line breaks were baked in.
   *   2. Consent could not actually be removed. It is woven mid-sentence through ACCURACY
   *      FACTORS and POTENTIAL INACCURACIES, and deleting those hand-wrapped lines would have
   *      left "Procedures are processed" with no ending.
   *   3. Any edit to a sentence meant re-wrapping every line under it by eye.
   *
   * Blocks are now paragraphs, wrapped to whatever the column width turns out to be and flowed
   * across the columns in order. Removing a block reflows the page; rewording one costs nothing.
   *
   * @param includeConsent when false, the two consent blocks are dropped and the three
   *   paragraphs that MENTION consent are swapped for versions that do not.
   */
  private addDisclaimerPage(
    doc: any,
    yPosition: number,
    margin: number,
    includeConsent: boolean = true,
  ): number {
    // Always start disclaimer on a new page
    doc.addPage();
    yPosition = 20;

    yPosition = this.addSectionHeader(doc, 'Data Accuracy & Calculation Methodology', yPosition, margin);

    doc.setFontSize(7);
    doc.setTextColor(0, 0, 0);

    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - (margin * 2);
    const lineHeight = 3.5;

    const COLUMNS = 4;
    const GUTTER = 6;
    const columnWidth = (contentWidth - (GUTTER * (COLUMNS - 1))) / COLUMNS;

    // Introduction text
    this.safeText(doc, 'This report contains detailed statistics calculated from this specific practice\'s data.', margin, yPosition);
    yPosition += lineHeight;
    this.safeText(doc, 'The following sections explain how each metric is calculated and what factors may affect data accuracy.', margin, yPosition);
    yPosition += lineHeight * 2;

    // ── The content ─────────────────────────────────────────────────────────────────
    //
    // `consentOnly` blocks vanish entirely; `bodyWithoutConsent` is the same explanation with the
    // consent sentences removed rather than truncated, so nothing ends mid-thought.
    const blocks: { heading: string; body: string; bodyWithoutConsent?: string; consentOnly?: boolean }[] = [
      {
        heading: 'PATIENT CONSULTATIONS',
        body: 'Patient consultations are defined as appointments scheduled through the MedicAI calendar '
          + 'system. This metric excludes all cancelled appointments and no-show appointments. The count '
          + 'represents only completed appointments that are marked as active records.',
      },
      {
        heading: 'AI TRANSCRIPTIONS',
        body: 'AI transcriptions represent AI-powered documentation requests generated for patient '
          + 'consultations. Only active AI requests are counted in this metric. Each AI request must be '
          + 'linked to an active patient record. The count is based on the creation timestamp of the AI '
          + 'request within the specified monthly period.',
      },
      {
        heading: 'AI SCRIBED PERCENTAGE',
        body: 'The AI scribed percentage represents the proportion of patient consultations that received '
          + 'AI assistance within a 48-hour window following the appointment. This calculation requires '
          + 'matching patient IDs between appointments and AI transcriptions. The system handles month '
          + 'boundaries automatically and provides each appointment with a precise 48-hour evaluation window.',
      },
      {
        heading: 'CLINICAL NOTES',
        body: 'Clinical notes encompass both AI-generated and manually created medical documentation. Only '
          + 'active notes are included in this metric. Each note must be linked to an active patient record. '
          + 'The count is determined by the creation timestamp within the specified monthly period.',
      },
      {
        heading: 'PROCEDURES',
        body: 'Procedures are defined as clinical procedures documented within patient notes. Only notes '
          + 'specifically marked as procedures are counted. Each procedure note must be linked to an active '
          + 'patient record. This metric represents actual procedures performed, as distinguished from '
          + 'appointment scheduling.',
      },
      {
        heading: 'DATA CONSIDERATIONS',
        body: 'All calculations are based exclusively on active records within the system. Monthly boundaries '
          + 'are calculated using exact start and end dates. Cross-month consent tracking prevents '
          + 'double-counting of consent records. AI transcription matching utilizes a 48-hour window with '
          + 'patient ID verification.',
        bodyWithoutConsent: 'All calculations are based exclusively on active records within the system. '
          + 'Monthly boundaries are calculated using exact start and end dates. AI transcription matching '
          + 'utilizes a 48-hour window with patient ID verification.',
      },
      {
        heading: 'ONBOARDING CONSENTS',
        consentOnly: true,
        body: 'Onboarding consent compliance tracking monitors consent status for patient appointments. The '
          + 'system utilizes a bundle of four consent documents that are signed together at the same time. '
          + 'Consent must be completed before or on the appointment date to be considered valid. The metric '
          + 'counts unique patients only and categorizes status as Completed, Pending, or Not Sent.',
      },
      {
        heading: 'PROCEDURE CONSENTS',
        consentOnly: true,
        body: 'Procedure consent compliance monitors consent status for procedures performed. The system '
          + 'operates within a 6-month timeframe window for consent validity. Each consent may only be used '
          + 'once per 12-month period. Procedures are processed in chronological order to ensure fair consent '
          + 'allocation. This system prevents consent reuse across multiple procedures within the same '
          + 'reporting period.',
      },
      {
        heading: 'ACCURACY FACTORS',
        body: 'Data accuracy is maintained through exclusive inclusion of active records within the system. '
          + 'Monthly boundaries are calculated using exact start and end dates. AI transcription matching '
          + 'utilizes a 48-hour window with patient ID verification. Cross-month consent tracking prevents '
          + 'double-counting. Procedures are processed in chronological order to ensure fair allocation of '
          + 'consent records.',
        bodyWithoutConsent: 'Data accuracy is maintained through exclusive inclusion of active records within '
          + 'the system. Monthly boundaries are calculated using exact start and end dates. AI transcription '
          + 'matching utilizes a 48-hour window with patient ID verification.',
      },
      {
        heading: 'POTENTIAL INACCURACIES',
        body: 'Potential data inaccuracies may arise from exclusion of inactive records from calculations. '
          + 'Late consent timing may affect compliance metrics. AI transcription delays beyond the 48-hour '
          + 'window are not captured. Only procedure notes are counted for procedure metrics. Consent form '
          + 'configuration and time zone considerations may impact data accuracy in certain scenarios.',
        bodyWithoutConsent: 'Potential data inaccuracies may arise from exclusion of inactive records from '
          + 'calculations. AI transcription delays beyond the 48-hour window are not captured. Only procedure '
          + 'notes are counted for procedure metrics. Time zone considerations may impact data accuracy in '
          + 'certain scenarios.',
      },
      {
        heading: 'SUPPORT & CONTACT',
        body: 'For questions regarding calculation methodologies or data accuracy concerns, please contact '
          + 'the support team. Additional methodology explanations and report verification services are '
          + 'available upon request. The support team can provide detailed explanations of any metric '
          + 'calculation or data accuracy factor.',
      },
    ];

    const visible = blocks
      .filter(block => includeConsent || !block.consentOnly)
      .map(block => {
        const body = (!includeConsent && block.bodyWithoutConsent) ? block.bodyWithoutConsent : block.body;
        const lines = this.wrapText(body, columnWidth, doc).split('\n').filter(l => l.trim());
        // +1 heading, +1 trailing gap — counted here so the balancer measures what is drawn.
        return { heading: block.heading, lines, height: lines.length + 2 };
      });

    // ── Balance across the columns, in order ────────────────────────────────────────
    //
    // In ORDER, not shortest-first: these are read top-to-bottom, left-to-right, and a packer
    // that optimised for even columns would scatter the explanations. Each column takes blocks
    // until adding the next would overshoot its share.
    //
    // The target is fixed at total/4 rather than recomputed per column as
    // `remaining / columnsLeft`. That was tried and MEASURED WORSE — recomputing under-fills the
    // early columns and dumps the remainder in the last one, taking the spread from 30% to 67%
    // on the consent-inclusive page. Measured spread here is ~30% with consent and ~35% without,
    // which for columns of prose reads as even.
    const totalHeight = visible.reduce((sum, b) => sum + b.height, 0);
    const target = Math.ceil(totalHeight / COLUMNS);

    const columns: (typeof visible)[] = Array.from({ length: COLUMNS }, () => []);
    let columnIndex = 0;
    let columnHeight = 0;
    for (const block of visible) {
      const wouldOvershoot = columnHeight > 0 && columnHeight + block.height > target;
      if (wouldOvershoot && columnIndex < COLUMNS - 1) {
        columnIndex += 1;
        columnHeight = 0;
      }
      columns[columnIndex].push(block);
      columnHeight += block.height;
    }

    // ── Draw ────────────────────────────────────────────────────────────────────────
    let lowestY = yPosition;
    columns.forEach((column, index) => {
      const columnX = margin + (columnWidth + GUTTER) * index;
      let y = yPosition;

      for (const block of column) {
        doc.setTextColor(0, 0, 0);
        this.safeText(doc, block.heading, columnX, y);
        y += lineHeight;

        doc.setTextColor(100, 100, 100);
        for (const line of block.lines) {
          this.safeText(doc, line, columnX, y);
          y += lineHeight;
        }

        y += lineHeight;
      }

      if (y > lowestY) lowestY = y;
    });

    doc.setTextColor(0, 0, 0);
    return lowestY + 10;
  }

  /**
   * Add footer with "Generated by" message
   */
  private addFooter(doc: any, margin: number): void {
    const pageHeight = doc.internal.pageSize.getHeight();

    const currentDateTime = new Date().toLocaleString('en-ZA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Africa/Johannesburg'
    });

    // Generate footer text using current user
    const footerText = `Generated by ${this.currentUser?.title || ''} ${this.currentUser?.name || ''} ${this.currentUser?.surname || ''} using MedicAI (contact@medicai.co.za) on ${currentDateTime}`.trim();

    // Set font and add footer text
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    this.safeText(doc, footerText, margin, pageHeight - 15);
  }
}
